import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { tap, first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { DictionaryItem, DictionaryItemDefinition, DictionarySimplePageComponent } from 'src/app/shared/components/dictionary-simple-page/dictionary-simple-page.component';
import { EsraPipingAPIClient, SpecificationViewModel, SpecificationType } from 'src/app/shared/models/autogenerated-piping';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { DictionaryExportService } from '../services/dictionary-export.service';
import { SpecificationAddEditComponent } from '../dialogs/specification-add-edit/specification-add-edit.component';

@Component({
    selector: 'app-specifications',
    templateUrl: '../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.html',
    styleUrls: ['../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.scss'],
    standalone: false
})
export class SpecificationsComponent extends DictionarySimplePageComponent<SpecificationViewModel> {
  constructor(
    private esraApiClient: EsraPipingAPIClient,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private insightsService: InsightsService,
    private dictionaryExportService: DictionaryExportService
  ) {
    super();
    this.exportFn = () => this.dictionaryExportService.exportSpecifications();
  }

  protected childOnInitBefore(): void { /* do nothing */ }
  protected childOnInitAfter(): void { /* do nothing */ }

  protected searchAll(): void {
    // Use the paged endpoint with showAll=true to load both active and inactive
    // records. The client-side filter predicate handles active/inactive visibility.
    this.esraApiClient.getAllSpecificationsPaged(undefined, undefined, undefined, true)
      .pipe(
        indicate(this.searching$),
        first(),
        tap((specifications: SpecificationViewModel[]) => {
          this.totalCount = specifications.length;
          this.dataSource.data = specifications;
        })
      ).subscribe();
  }

  protected initializeDictionaryDefinition(): DictionaryItemDefinition<SpecificationViewModel> {
    return {
      displayStatusColumn: true,
      displayDeleteButton: true,
      dictionaryItems: [
        new DictionaryItem('Last Updated',   'updated',             'standard', (item: SpecificationViewModel) => this.getDate(item)),
        new DictionaryItem('Spec Number',    'specificationNumber', 'standard'),
        new DictionaryItem('Spec Title',     'specificationTitle',  'extended'),
        // businessUnitName and applicationName are read-only display fields returned by the API on GET.
        new DictionaryItem('Business Unit',  'businessUnitName',    'standard'),
        new DictionaryItem('Application',    'applicationName',     'standard'),
        new DictionaryItem('Type',           'type',                'standard', (item: SpecificationViewModel) => this.getTypeName(item.type)),
        new DictionaryItem('Name (legacy)',  'name',                'standard'),
      ]
    };
  }

  protected getDate(item: SpecificationViewModel): string {
    return String(item.updated.getMonth() + 1).padStart(2, '0') + '/' +
           String(item.updated.getDate()).padStart(2, '0') + '/' +
           item.updated.getFullYear();
  }

  /** Maps the numeric SpecificationType enum value to its human-readable label. */
  protected getTypeName(type: SpecificationType | undefined): string {
    if (type === SpecificationType._0) { return 'Piping'; }
    if (type === SpecificationType._1) { return 'Valve'; }
    return '';
  }

  protected getFilterPredicate(record: SpecificationViewModel, parametersMap: Map<string, string>): boolean {
    const searchQuery: string = parametersMap.get(DictionarySimplePageComponent.SEARCH_QUERY_NAME)?.toLowerCase() || '';
    const isActiveFilter: string = parametersMap.get(DictionarySimplePageComponent.IS_ACTIVE_NAME);

    // Search across all text-based fields, including the newly searchable specificationNumber
    // and specificationTitle fields that the backend also searches by in paged queries.
    // businessUnitName and applicationName are the read-only display strings returned by GET.
    const matchesSearchQuery: boolean =
      (record.name?.toLowerCase().includes(searchQuery) ?? false) ||
      (record.specificationNumber?.toLowerCase().includes(searchQuery) ?? false) ||
      (record.specificationTitle?.toLowerCase().includes(searchQuery) ?? false) ||
      (record.businessUnitName?.toLowerCase().includes(searchQuery) ?? false) ||
      (record.applicationName?.toLowerCase().includes(searchQuery) ?? false) ||
      record.updated.toString().toLowerCase().includes(searchQuery);

    if (isActiveFilter == null) {
      return matchesSearchQuery;
    } else {
      return matchesSearchQuery && record.isActive === JSON.parse(isActiveFilter);
    }
  }

  protected getNoMatchMessage(): string {
    return 'No specifications match provided search criteria';
  }

  protected createNew(): void {
    this.openEditor(null);
  }

  protected editItem(specification: SpecificationViewModel): void {
    this.openEditor(specification);
  }

  protected editItemReferenced(specification: SpecificationViewModel): void {
    // Referenced specifications can still be edited — only deactivation is blocked.
    this.editItem(specification);
  }

  private openEditor(specification: SpecificationViewModel | null): void {
    const dialogRef: MatDialogRef<SpecificationAddEditComponent, boolean> = this.dialog.open<
      SpecificationAddEditComponent,
      SpecificationViewModel | null,
      boolean
    >(SpecificationAddEditComponent, {
      width: '700px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
      data: specification
    });

    dialogRef.afterClosed().pipe(first()).subscribe((saved: boolean | undefined) => {
      if (saved) {
        this.searchAll();
      }
    });
  }

  protected deleteItem(item: SpecificationViewModel): void {
    const hasAssociations = (item as any)['hasAssociations'];
    const displayName: string = item.specificationNumber || item.name || 'Unknown';

    if (hasAssociations) {
      this.snackBarService.showError(`Specification "${displayName}" could not be deleted because it has associated items.`);
      return;
    }

    this.esraApiClient.deleteSpecification(item.id).subscribe({
      next: () => {
        const index: number = this.dataSource.data.findIndex(s => s.id === item.id);
        if (index !== -1) {
          this.dataSource.data.splice(index, 1);
          this.dataSource.data = [...this.dataSource.data];
        }
        this.snackBarService.showSnackBar(
          true,
          `Specification "${displayName}" was successfully deleted.`,
          'mat-snack-bar-info'
        );
      },
      error: (error) => {
        this.snackBarService.showSnackBar(
          true,
          `Sorry, something went wrong deleting specification "${displayName}".`,
          'mat-snack-bar-error'
        );
        this.insightsService.logException(error);
      }
    });
  }

  protected updateStatus(item: SpecificationViewModel): void {
    const displayName: string = item.specificationNumber || item.name || 'Unknown';

    if (item.isActive && item.isReferenced) {
      this.snackBarService.showError('Cannot deactivate a specification referenced by a Release.');
      return;
    }

    this.esraApiClient.toggleSpecificationStatus(item.id).subscribe({
      next: (updated: SpecificationViewModel) => {
        const index: number = this.dataSource.data.findIndex(s => s.id === item.id);
        if (index !== -1) {
          this.dataSource.data[index] = updated;
          this.dataSource.data = [...this.dataSource.data];
        }
        const newState: string = item.isActive ? 'deactivated' : 'activated';
        this.snackBarService.showSnackBar(
          true,
          `Specification "${displayName}" was successfully ${newState}.`,
          'mat-snack-bar-info'
        );
        this.search();
      },
      error: (error) => {
        this.snackBarService.showSnackBar(
          true,
          `Sorry, something went wrong toggling specification "${displayName}".`,
          'mat-snack-bar-error'
        );
        this.insightsService.logException(error);
      }
    });
  }

  protected getUsageInformation(_item: object): void { /* not applicable for specifications */ }
}
